import React, { useState } from 'react'

export const UploadFileForm = ({ onUploadFile, onCancel, targetFolder }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFileSelect = (file) => {
    // Comprehensive validation
    if (!file) {
      console.warn('No file provided')
      setSelectedFile(null)
      return
    }

    if (!file.name || typeof file.name !== 'string') {
      console.warn('Invalid file selected - missing or invalid name:', file)
      setSelectedFile(null)
      alert('Invalid file. Please select a file with a valid name.')
      return
    }

    // Additional validation for file properties
    if (file.size === undefined || file.size === null) {
      console.warn('File missing size property:', file)
      // Don't reject, just set size to 0
      file.size = 0
    }

    setSelectedFile(file)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Comprehensive validation
    if (!selectedFile) {
      alert('Please select a file to upload')
      return
    }

    if (!selectedFile.name || typeof selectedFile.name !== 'string') {
      alert(
        'Selected file has an invalid name. Please select a different file.'
      )
      return
    }

    // Handle real file upload
    try {
      const fileData = {
        name: selectedFile.name,
        size: formatFileSize(selectedFile.size || 0),
        type: selectedFile.file_type || 'application/octet-stream',
        url: selectedFile.file_url,
        file: selectedFile,
      }

      // Final validation before upload
      if (!fileData.name) {
        throw new Error('File data validation failed')
      }

      onUploadFile(fileData)
    } catch (error) {
      console.error('Error uploading file:', error)
      alert(
        `Error uploading file: ${selectedFile.name || 'Unknown'}. Please try again.`
      )
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file && file.name) {
        handleFileSelect(file)
      } else {
        console.warn('Invalid file dropped:', file)
        alert('Invalid file. Please select a valid file.')
      }
    }
  }

  const handleFileInputChange = (e) => {
    if (e.target?.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file && file.name) {
        handleFileSelect(file)
      } else {
        console.warn('Invalid file selected:', file)
        alert('Invalid file. Please select a valid file.')
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold text-black mb-4">
          Upload File {targetFolder && `to ${targetFolder.name}`}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
              dragActive
                ? 'border-[#3d6be0] bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div>
                <div className="text-4xl mb-2">📎</div>
                <p className="font-medium text-gray-800">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="text-red-500 hover:underline mt-2"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-2">📤</div>
                <p className="text-gray-600 mb-2">
                  Drag and drop your file here
                </p>
                <p className="text-gray-500 text-sm mb-4">or</p>
                <label className="bg-[#3d6be0] text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-600">
                  Choose File
                  <input
                    type="file"
                    onChange={handleFileInputChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.txt,.md,.mp4,.avi,.mov,.wmv,.mp3,.wav,.ogg"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Supported file types */}
          <p className="text-xs text-gray-500 mb-4">
            Supported formats: PDF, DOC, DOCX, XLS, XLSX, CSV, PPT, PPTX, JPG,
            PNG, GIF, TXT, MD, MP4, MP3, and more
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile}
              className="flex-1 px-4 py-2 bg-[#3d6be0] text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Upload File
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// import React, { useState } from 'react'
// import axios from 'axios'
// import ApiClient from '../../../../api/client'
// import { useParams } from 'react-router-dom'

// const apiClient = new ApiClient()

// export const UploadFileForm = ({ onUploadSuccess, onCancel, targetFolder }) => {
//   const { memberId } = useParams()
//   const [selectedFile, setSelectedFile] = useState([])
//   const [isUploading, setIsUploading] = useState(false)
//   const [dragActive, setDragActive] = useState(false)

//   const handleFileSelect = (file) => {
//     if (!file) return
//     setSelectedFile(file)
//   }

//   const handleFileInputChange = (e) => {
//     if (e.target.files && e.target.files[0]) {
//       handleFileSelect(e.target.files[0])
//     }
//   }

//   const handleDrag = (e) => {
//     e.preventDefault()
//     e.stopPropagation()
//     if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
//     else if (e.type === 'dragleave') setDragActive(false)
//   }

//   const handleDrop = (e) => {
//     e.preventDefault()
//     e.stopPropagation()
//     setDragActive(false)
//     if (e.dataTransfer.files && e.dataTransfer.files[0]) {
//       handleFileSelect(e.dataTransfer.files[0])
//     }
//   }

//   const handleSubmit = async (e) => {
//     e.preventDefault()
//     if (!selectedFile) {
//       alert('Please select a file to upload')
//       return
//     }

//     // const formData = new FormData()
//     // formData.append('file', selectedFile)
//     // if (targetFolder?.path) formData.append('path', targetFolder.path)

//     try {
//       setIsUploading(true)
//       const response = await apiClient.uploadFile(memberId, selectedFile)
//       // onUploadSuccess(response.data)
//       console.log(response.data)
//     } catch (error) {
//       console.error(error)
//       alert('File upload failed')
//     } finally {
//       setIsUploading(false)
//     }
//   }

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
//         <h2 className="text-xl font-bold text-gray-900 mb-4">
//           Upload File {targetFolder && `to ${targetFolder.name}`}
//         </h2>

//         <form onSubmit={handleSubmit}>
//           <div
//             onDragEnter={handleDrag}
//             onDragLeave={handleDrag}
//             onDragOver={handleDrag}
//             onDrop={handleDrop}
//             className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition ${
//               dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
//             }`}
//           >
//             {selectedFile ? (
//               <div>
//                 <p className="text-lg font-medium text-gray-800">
//                   {selectedFile.name}
//                 </p>
//                 <p className="text-sm text-gray-500">
//                   {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
//                 </p>
//                 <button
//                   type="button"
//                   onClick={() => setSelectedFile(null)}
//                   className="text-red-500 hover:underline mt-2"
//                 >
//                   Remove file
//                 </button>
//               </div>
//             ) : (
//               <div>
//                 <div className="text-4xl mb-2">📤</div>
//                 <p className="text-gray-600 mb-2">Drag & drop your file here</p>
//                 <p className="text-gray-500 text-sm mb-4">or</p>
//                 <label className="bg-blue-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-blue-700">
//                   Choose File
//                   <input
//                     type="file"
//                     onChange={handleFileInputChange}
//                     className="hidden"
//                     accept="*/*"
//                   />
//                 </label>
//               </div>
//             )}
//           </div>

//           <div className="flex gap-3">
//             <button
//               type="button"
//               onClick={onCancel}
//               className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               disabled={!selectedFile || isUploading}
//               className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
//             >
//               {isUploading ? 'Uploading...' : 'Upload File'}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   )
// }
